import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { z } from 'zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { mergeFinancialSupportWizard, readFinancialSupportWizard } from './financialSupportProgramWizardStorage';
import AlertToast from '../../components/alertToast';
import {
  FinancialSupportReadOnlyDetail,
  FinancialSupportSectionHeader,
  FinancialSupportWizardSidebar,
  wizardContentGridSx,
  wizardDependentFieldTextSx,
  wizardDependentFieldWrapSx,
  wizardPageSubtitleSx,
  wizardPageTitleSx,
  wizardPositiveActionButtonSx,
  wizardPrimaryButtonSx,
  wizardSecondaryButtonSx,
  wizardSectionCardSx,
  wizardSidebarGridSx,
  wizardStickyFooterSx,
  wizardTextActionButtonSx,
  wizardWidePageShellSx,
} from '../../components/financialSupportWizard';
import {
  FieldLabel,
  createMenuProps,
  helperTextSx,
  placeholderTextSx,
} from '../../styles/formStyles';
import {
  accordionExpandIconSx,
  mediumMaterialIconSx,
  primaryIconColor,
} from '../../styles/iconStyles';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    minHeight: 48,
    '& fieldset': { borderColor: '#E4E6F1' },
    '&:hover fieldset': { borderColor: '#E4E6F1' },
    '&.Mui-focused fieldset': { borderColor: '#294190' },
    '&.Mui-disabled': { backgroundColor: '#F7F7F9' },
  },
  '& .MuiOutlinedInput-input': {
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    color: '#1C1B1F',
    fontWeight: 400,
    py: 1.5,
    '&::placeholder': {
      color: '#625B71',
      opacity: 1,
      fontSize: 16,
      lineHeight: '24px',
      letterSpacing: '0.5px',
      fontWeight: 400,
    },
    '&.Mui-disabled': {
      WebkitTextFillColor: '#8C8B94',
    },
  },
  '& .MuiSelect-select': {
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    color: '#1C1B1F',
    fontWeight: 400,
    py: 1.5,
  },
  '& .MuiInputAdornment-root': {
    color: '#1C1B1F',
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  },
};

const menuProps = createMenuProps();

const applicabilityControlSx = {
  width: '100%',
  gap: '8px',
  '& .MuiToggleButton-root': {
    height: 40,
    px: 2,
    borderRadius: '999px !important',
    border: '1px solid #D7DDEA',
    textTransform: 'none',
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    fontWeight: 500,
    color: '#4A4459',
    backgroundColor: '#FFFFFF',
  },
  '& .MuiToggleButton-root.Mui-selected': {
    color: '#1C1B1F',
    backgroundColor: '#EEF3FF',
    borderColor: '#294190',
  },
  '& .MuiToggleButton-root.Mui-selected:hover': {
    backgroundColor: '#EEF3FF',
  },
};

const supportTypeOptions = [
  { value: 'CREDIT', label: 'Credit' },
  { value: 'GRANT_SUBSIDY', label: 'Grant/Subsidy' },
  { value: 'INVESTMENT', label: 'Investment' },
];
const currencyOptions = ['INR', 'USD', 'KES', 'NGN'];
const planStatusOptions = ['Draft', 'Active', 'Inactive'];
const paymentFrequencyOptions = ['Weekly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly', 'Custom'];
const yesNoOptions = ['Yes', 'No'];
const disbursementScheduleOptions = ['One-time', 'Milestone-based', 'Reimbursement-based', 'Tranche-based', 'Custom'];
const stepItems = [
  { id: 1, label: 'Basic Info', completed: true },
  { id: 2, label: 'Contribution Rules', completed: true },
  { id: 3, label: 'Plans', active: true },
  { id: 4, label: 'Review & Publish' },
];

const numericFieldKeys = [
  'planAmount',
  'interestRate',
  'repaymentDuration',
  'numberOfInstallments',
  'gracePeriod',
  'processingFeeAmount',
  'latePaymentFeeAmount',
  'maximumCoveragePercentage',
  'matchingContributionPercentage',
  'equityRevenueShare',
  'expectedReturnRate',
  'exitVestingPeriod',
];

const applicabilityFieldNames = [
  'interestRateApplicable',
  'repaymentDurationApplicable',
  'numberOfInstallmentsApplicable',
  'paymentFrequencyApplicable',
  'gracePeriodApplicable',
  'processingFeeAmountApplicable',
  'latePaymentFeeAmountApplicable',
  'collateralRequiredApplicable',
  'maximumCoveragePercentageApplicable',
  'matchingContributionRequiredApplicable',
  'disbursementScheduleApplicable',
  'equityRevenueShareApplicable',
  'expectedReturnRateApplicable',
  'exitVestingPeriodApplicable',
];

const plansSchema = z.object({
  planName: z.string().optional(),
  supportType: z.string().min(1, { message: 'Support Type is required' }),
  planAmount: z.string().optional(),
  currency: z.string().min(1, { message: 'Currency is required' }),
  planStatus: z.string().optional(),
  description: z.string().optional(),
  interestRateApplicable: z.boolean(),
  interestRate: z.string().optional(),
  repaymentDurationApplicable: z.boolean(),
  repaymentDuration: z.string().optional(),
  numberOfInstallmentsApplicable: z.boolean(),
  numberOfInstallments: z.string().optional(),
  paymentFrequencyApplicable: z.boolean(),
  paymentFrequency: z.string().optional(),
  gracePeriodApplicable: z.boolean(),
  gracePeriod: z.string().optional(),
  processingFeeAmountApplicable: z.boolean(),
  processingFeeAmount: z.string().optional(),
  latePaymentFeeAmountApplicable: z.boolean(),
  latePaymentFeeAmount: z.string().optional(),
  collateralRequiredApplicable: z.boolean(),
  collateralRequired: z.string().optional(),
  collateralDetails: z.string().optional(),
  maximumCoveragePercentageApplicable: z.boolean(),
  maximumCoveragePercentage: z.string().optional(),
  matchingContributionRequiredApplicable: z.boolean(),
  matchingContributionRequired: z.string().optional(),
  matchingContributionPercentage: z.string().optional(),
  disbursementScheduleApplicable: z.boolean(),
  disbursementSchedule: z.string().optional(),
  customDisbursementSchedule: z.string().optional(),
  equityRevenueShareApplicable: z.boolean(),
  equityRevenueShare: z.string().optional(),
  expectedReturnRateApplicable: z.boolean(),
  expectedReturnRate: z.string().optional(),
  exitVestingPeriodApplicable: z.boolean(),
  exitVestingPeriod: z.string().optional(),
  eligibilityCriteria: z.string().optional(),
  disbursementConditions: z.string().optional(),
  repaymentNotes: z.string().optional(),
  otherConditions: z.string().optional(),
}).superRefine((data, ctx) => {
  const issue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
  const requireNumber = (field, label, { min = 0, max = Infinity, allowZero = true } = {}) => {
    const raw = String(data[field] ?? '').trim();
    if (!raw) {
      issue(field, `${label} is required`);
      return;
    }
    const value = Number(raw);
    if (Number.isNaN(value)) {
      issue(field, `${label} must be a valid number`);
      return;
    }
    if (!allowZero && value <= 0) {
      issue(field, `${label} must be greater than 0`);
      return;
    }
    if (value < min) {
      issue(field, `${label} cannot be less than ${min}`);
      return;
    }
    if (value > max) {
      issue(field, `${label} cannot be greater than ${max}`);
    }
  };

  if (String(data.planAmount ?? '').trim()) {
    requireNumber('planAmount', 'Plan Amount', { min: 0, allowZero: false });
  }
  if (data.interestRateApplicable) requireNumber('interestRate', 'Interest Rate', { min: 0, max: 100 });
  if (data.repaymentDurationApplicable) requireNumber('repaymentDuration', 'Repayment Duration', { min: 0, allowZero: false });
  if (data.numberOfInstallmentsApplicable) requireNumber('numberOfInstallments', 'Number of Installments', { min: 0, allowZero: false });
  if (data.paymentFrequencyApplicable && !data.paymentFrequency) issue('paymentFrequency', 'Payment Frequency is required');
  if (data.gracePeriodApplicable) requireNumber('gracePeriod', 'Grace Period', { min: 0 });
  if (data.processingFeeAmountApplicable) requireNumber('processingFeeAmount', 'Processing Fee Amount', { min: 0 });
  if (data.latePaymentFeeAmountApplicable) requireNumber('latePaymentFeeAmount', 'Late Payment Fee Amount', { min: 0 });
  if (data.collateralRequiredApplicable && !data.collateralRequired) issue('collateralRequired', 'Collateral selection is required');
  if (data.collateralRequiredApplicable && data.collateralRequired === 'Yes' && !String(data.collateralDetails ?? '').trim()) issue('collateralDetails', 'Collateral Details are required');
  if (data.maximumCoveragePercentageApplicable) requireNumber('maximumCoveragePercentage', 'Maximum Coverage Percentage', { min: 0, max: 100 });
  if (data.matchingContributionRequiredApplicable && !data.matchingContributionRequired) issue('matchingContributionRequired', 'Matching Contribution selection is required');
  if (data.matchingContributionRequiredApplicable && data.matchingContributionRequired === 'Yes') requireNumber('matchingContributionPercentage', 'Matching Contribution Percentage', { min: 0, max: 100 });
  if (data.disbursementScheduleApplicable && !data.disbursementSchedule) issue('disbursementSchedule', 'Disbursement Schedule is required');
  if (data.disbursementScheduleApplicable && data.disbursementSchedule === 'Custom' && !String(data.customDisbursementSchedule ?? '').trim()) issue('customDisbursementSchedule', 'Custom Schedule explanation is required');
  if (data.equityRevenueShareApplicable) requireNumber('equityRevenueShare', 'Equity / Revenue Share', { min: 0, max: 100 });
  if (data.expectedReturnRateApplicable) requireNumber('expectedReturnRate', 'Expected Return Rate', { min: 0, max: 100 });
  if (data.exitVestingPeriodApplicable) requireNumber('exitVestingPeriod', 'Exit / Vesting Period', { min: 0, allowZero: false });
});

function getSupportTypePreset(supportType) {
  const presets = {
    CREDIT: {
      interestRateApplicable: true,
      repaymentDurationApplicable: true,
      numberOfInstallmentsApplicable: true,
      paymentFrequencyApplicable: true,
      gracePeriodApplicable: true,
      processingFeeAmountApplicable: true,
      latePaymentFeeAmountApplicable: true,
      collateralRequiredApplicable: true,
      maximumCoveragePercentageApplicable: false,
      matchingContributionRequiredApplicable: false,
      disbursementScheduleApplicable: false,
      equityRevenueShareApplicable: false,
      expectedReturnRateApplicable: false,
      exitVestingPeriodApplicable: false,
    },
    GRANT_SUBSIDY: {
      interestRateApplicable: false,
      repaymentDurationApplicable: false,
      numberOfInstallmentsApplicable: false,
      paymentFrequencyApplicable: false,
      gracePeriodApplicable: false,
      processingFeeAmountApplicable: false,
      latePaymentFeeAmountApplicable: false,
      collateralRequiredApplicable: false,
      maximumCoveragePercentageApplicable: true,
      matchingContributionRequiredApplicable: true,
      disbursementScheduleApplicable: true,
      equityRevenueShareApplicable: false,
      expectedReturnRateApplicable: false,
      exitVestingPeriodApplicable: false,
    },
    INVESTMENT: {
      interestRateApplicable: false,
      repaymentDurationApplicable: false,
      numberOfInstallmentsApplicable: false,
      paymentFrequencyApplicable: false,
      gracePeriodApplicable: false,
      processingFeeAmountApplicable: false,
      latePaymentFeeAmountApplicable: false,
      collateralRequiredApplicable: false,
      maximumCoveragePercentageApplicable: false,
      matchingContributionRequiredApplicable: false,
      disbursementScheduleApplicable: true,
      equityRevenueShareApplicable: true,
      expectedReturnRateApplicable: true,
      exitVestingPeriodApplicable: true,
    },
  };

  return presets[supportType] || {};
}

function createPlanDefaults(basicInfo = {}) {
  return {
    planName: '',
    supportType: basicInfo.supportType || '',
    planAmount: '',
    currency: basicInfo.currency || '',
    planStatus: 'Draft',
    description: '',
    interestRateApplicable: false,
    interestRate: '',
    repaymentDurationApplicable: false,
    repaymentDuration: '',
    numberOfInstallmentsApplicable: false,
    numberOfInstallments: '',
    paymentFrequencyApplicable: false,
    paymentFrequency: '',
    gracePeriodApplicable: false,
    gracePeriod: '',
    processingFeeAmountApplicable: false,
    processingFeeAmount: '',
    latePaymentFeeAmountApplicable: false,
    latePaymentFeeAmount: '',
    collateralRequiredApplicable: false,
    collateralRequired: '',
    collateralDetails: '',
    maximumCoveragePercentageApplicable: false,
    maximumCoveragePercentage: '',
    matchingContributionRequiredApplicable: false,
    matchingContributionRequired: '',
    matchingContributionPercentage: '',
    disbursementScheduleApplicable: false,
    disbursementSchedule: '',
    customDisbursementSchedule: '',
    equityRevenueShareApplicable: false,
    equityRevenueShare: '',
    expectedReturnRateApplicable: false,
    expectedReturnRate: '',
    exitVestingPeriodApplicable: false,
    exitVestingPeriod: '',
    eligibilityCriteria: '',
    disbursementConditions: '',
    repaymentNotes: '',
    otherConditions: '',
    ...getSupportTypePreset(basicInfo.supportType),
  };
}

function normalizePlanData(data) {
  const normalized = {
    ...data,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  };

  numericFieldKeys.forEach((key) => {
    const raw = normalized[key];
    normalized[key] = raw === '' || raw === undefined ? null : Number(raw);
  });

  if (!normalized.paymentFrequencyApplicable) normalized.paymentFrequency = null;
  if (!normalized.collateralRequiredApplicable) normalized.collateralRequired = null;
  if (!(normalized.collateralRequiredApplicable && normalized.collateralRequired === 'Yes')) normalized.collateralDetails = null;
  if (!normalized.matchingContributionRequiredApplicable) normalized.matchingContributionRequired = null;
  if (!(normalized.matchingContributionRequiredApplicable && normalized.matchingContributionRequired === 'Yes')) normalized.matchingContributionPercentage = null;
  if (!normalized.disbursementScheduleApplicable) normalized.disbursementSchedule = null;
  if (!(normalized.disbursementScheduleApplicable && normalized.disbursementSchedule === 'Custom')) normalized.customDisbursementSchedule = null;

  applicabilityFieldNames.forEach((key) => {
    if (!normalized[key]) {
      const valueField = key.replace('Applicable', '');
      if (Object.prototype.hasOwnProperty.call(normalized, valueField)) {
        normalized[valueField] = null;
      }
    }
  });

  return normalized;
}

function isPlanDraftEmpty(values) {
  const meaningfulKeys = [
    'planName',
    'planAmount',
    'description',
    'interestRate',
    'repaymentDuration',
    'numberOfInstallments',
    'paymentFrequency',
    'gracePeriod',
    'processingFeeAmount',
    'latePaymentFeeAmount',
    'collateralRequired',
    'collateralDetails',
    'maximumCoveragePercentage',
    'matchingContributionRequired',
    'matchingContributionPercentage',
    'disbursementSchedule',
    'customDisbursementSchedule',
    'equityRevenueShare',
    'expectedReturnRate',
    'exitVestingPeriod',
    'eligibilityCriteria',
    'disbursementConditions',
    'repaymentNotes',
    'otherConditions',
  ];

  return !meaningfulKeys.some((key) => String(values[key] ?? '').trim());
}

function ApplicabilityFieldRow({ control, applicableName, label, helperText, children }) {
  return (
    <Grid container spacing={2.5} alignItems="start" sx={{ py: 2.5 }}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Typography sx={{ fontSize: 15, lineHeight: '22px', letterSpacing: '0.1px', fontWeight: 600, color: '#1C1B1F', mb: 0.75 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 13, lineHeight: '18px', letterSpacing: '0.1px', color: '#6B6880' }}>
          {helperText}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 3 }}>
        <Controller
          name={applicableName}
          control={control}
          render={({ field }) => (
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={field.value ? 'applicable' : 'not_applicable'}
              onChange={(_, value) => {
                if (!value) return;
                field.onChange(value === 'applicable');
              }}
              aria-label={`${label} applicability`}
              sx={applicabilityControlSx}
            >
              <ToggleButton value="applicable">Applicable</ToggleButton>
              <ToggleButton value="not_applicable">N/A</ToggleButton>
            </ToggleButtonGroup>
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        {children}
      </Grid>
    </Grid>
  );
}


export default function CreateFinancialSupportProgramPlans() {
  const navigate = useNavigate();
  const wizardData = readFinancialSupportWizard();
  const basicInfo = wizardData.basicInfo || {};
  const [savedPlans, setSavedPlans] = useState(Array.isArray(wizardData.plans) ? wizardData.plans : []);
  const [expandedPanel, setExpandedPanel] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    getValues,
    trigger,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(plansSchema),
    defaultValues: createPlanDefaults(basicInfo),
  });

  const watchedValues = useWatch({
    control,
    name: [
      'supportType',
      'currency',
      'interestRateApplicable',
      'repaymentDurationApplicable',
      'numberOfInstallmentsApplicable',
      'paymentFrequencyApplicable',
      'gracePeriodApplicable',
      'processingFeeAmountApplicable',
      'latePaymentFeeAmountApplicable',
      'collateralRequiredApplicable',
      'collateralRequired',
      'maximumCoveragePercentageApplicable',
      'matchingContributionRequiredApplicable',
      'matchingContributionRequired',
      'disbursementScheduleApplicable',
      'disbursementSchedule',
      'equityRevenueShareApplicable',
      'expectedReturnRateApplicable',
      'exitVestingPeriodApplicable',
    ],
  });

  const [
    watchedSupportType,
    watchedCurrency,
    interestRateApplicable,
    repaymentDurationApplicable,
    numberOfInstallmentsApplicable,
    paymentFrequencyApplicable,
    gracePeriodApplicable,
    processingFeeAmountApplicable,
    latePaymentFeeAmountApplicable,
    collateralRequiredApplicable,
    collateralRequired,
    maximumCoveragePercentageApplicable,
    matchingContributionRequiredApplicable,
    matchingContributionRequired,
    disbursementScheduleApplicable,
    disbursementSchedule,
    equityRevenueShareApplicable,
    expectedReturnRateApplicable,
    exitVestingPeriodApplicable,
  ] = watchedValues;

  const emphasizedSections = useMemo(() => ({
    repayment: watchedSupportType === 'CREDIT',
    grant: watchedSupportType === 'GRANT_SUBSIDY',
    investment: watchedSupportType === 'INVESTMENT',
  }), [watchedSupportType]);

  const applySupportTypePreset = (supportType) => {
    const preset = getSupportTypePreset(supportType);
    Object.entries(preset).forEach(([name, value]) => {
      setValue(name, value, { shouldDirty: true, shouldValidate: false });
    });
  };

  const resetForNextPlan = (supportType, currency) => {
    reset(createPlanDefaults({
      supportType: basicInfo.supportType || supportType,
      currency: basicInfo.currency || currency,
    }));
  };

  const persistPlans = (plans) => {
    mergeFinancialSupportWizard({ plans });
  };

  const renderTextField = (name, placeholder, adornment, disabled = false, multiline = false, minRows = 1) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          disabled={disabled}
          multiline={multiline}
          minRows={multiline ? minRows : undefined}
          placeholder={disabled ? 'Not applicable' : placeholder}
          error={!!errors[name]}
          helperText={errors[name]?.message || ''}
          sx={{ ...fieldSx, ...helperTextSx }}
          InputProps={adornment ? { endAdornment: <InputAdornment position="end">{adornment}</InputAdornment> } : undefined}
        />
      )}
    />
  );

  const renderSelectField = (name, options, placeholder, disabled = false, onValueChange) => (
    <FormControl fullWidth error={!!errors[name]} sx={helperTextSx}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            displayEmpty
            disabled={disabled}
            MenuProps={menuProps}
            IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
            sx={fieldSx}
            onChange={(event) => {
              field.onChange(event);
              if (onValueChange) {
                onValueChange(event.target.value);
              }
            }}
            renderValue={(selected) => !selected ? <Box sx={placeholderTextSx}>{disabled ? 'Not applicable' : placeholder}</Box> : selected}
          >
            {options.map((item) => {
              const itemValue = typeof item === 'object' ? item.value : item;
              const itemLabel = typeof item === 'object' ? item.label : item;
              return <MenuItem key={itemValue} value={itemValue}>{itemLabel}</MenuItem>;
            })}
          </Select>
        )}
      />
      {!!errors[name] && <FormHelperText error>{errors[name]?.message}</FormHelperText>}
    </FormControl>
  );

  const addAnotherPlan = handleSubmit((data) => {
    const nextPlans = [...savedPlans, normalizePlanData(data)];
    setSavedPlans(nextPlans);
    setExpandedPanel(false);
    persistPlans(nextPlans);
    resetForNextPlan(data.supportType, data.currency);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSnackbarMessage(`"${data.planName}" added. You can configure another plan below.`);
  });

  const goToReview = async () => {
    const currentValues = getValues();
    const currentFormIsEmpty = !isDirty && isPlanDraftEmpty(currentValues);

    if (currentFormIsEmpty) {
      if (!savedPlans.length) {
        await trigger();
        setSnackbarMessage('Add at least one plan before moving to review.');
        return;
      }

      persistPlans(savedPlans);
      navigate('/financialSupport/create-program/review');
      return;
    }

    handleSubmit((data) => {
      const nextPlans = [...savedPlans, normalizePlanData(data)];
      persistPlans(nextPlans);
      navigate('/financialSupport/create-program/review');
    })();
  };

  return (
    <Box sx={wizardWidePageShellSx}>
      <Typography className="app-page-title" sx={{ ...wizardPageTitleSx, mb: 1 }}>
        Financial Plan Configuration
      </Typography>
      <Typography sx={{ ...wizardPageSubtitleSx, mb: 4.5, maxWidth: 980 }}>
        Configure financial support plans for credit, grant, subsidy, and investment programs using a single master template. For each optional field, specify whether it applies to this plan. Applicable fields require values; non-applicable fields remain disabled.
      </Typography>

      <Box className="material-form">
        <Grid container spacing={3} alignItems="stretch">
          <Grid size={{ xs: 12, md: 3.2, lg: 3 }} sx={wizardSidebarGridSx}>
            <FinancialSupportWizardSidebar steps={stepItems} />
          </Grid>
          <Grid size={{ xs: 12, md: 8.8, lg: 9 }} sx={wizardContentGridSx}>
            <Stack spacing={3} sx={{ width: '100%' }}>
              {savedPlans.length > 0 && (
                <Box sx={wizardSectionCardSx}>
                  <FinancialSupportSectionHeader
                    title="Configured Plans"
                    helperText="These plans are already attached to this financial support program. Expand any plan to review the summary while keeping the editor below focused on the next plan."
                  />
                  <Stack spacing={1.5}>
                    {savedPlans.map((plan, index) => (
                      <Accordion
                        key={plan.id}
                        disableGutters
                        expanded={expandedPanel === plan.id}
                        onChange={(_, expanded) => setExpandedPanel(expanded ? plan.id : false)}
                        sx={{
                          border: '1px solid #DFE5F6',
                          borderRadius: '16px !important',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<Box component="span" className="material-symbols-rounded" sx={accordionExpandIconSx}>expand_more</Box>}
                          sx={{ px: 2.5, py: 1.25 }}
                        >
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ width: '100%', pr: 1 }}>
                            <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: '#1C1B1F' }}>
                              Plan {index + 1}: {plan.planName}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Chip label={plan.supportType} size="small" sx={{ bgcolor: '#EEF3FF', color: '#294190', fontWeight: 600 }} />
                              <Chip label={plan.currency} size="small" sx={{ bgcolor: '#F1F1F1', color: '#1C1B1F' }} />
                              <Chip label={plan.planStatus} size="small" sx={{ bgcolor: '#EEF8DE', color: '#4F8506', fontWeight: 600 }} />
                            </Stack>
                            <Typography sx={{ ml: { md: 'auto' }, fontSize: 15, lineHeight: '22px', color: '#1C1B1F', fontWeight: 600 }}>
                              Amount: {plan.currency} {plan.planAmount}
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                          <Grid container spacing={2.5}>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}><FinancialSupportReadOnlyDetail label="Description / Notes" value={plan.description} /></Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}><FinancialSupportReadOnlyDetail label="Interest Rate" value={plan.interestRateApplicable ? `${plan.interestRate}%` : 'Not applicable'} /></Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}><FinancialSupportReadOnlyDetail label="Repayment Duration" value={plan.repaymentDurationApplicable ? `${plan.repaymentDuration} months` : 'Not applicable'} /></Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}><FinancialSupportReadOnlyDetail label="Coverage Percentage" value={plan.maximumCoveragePercentageApplicable ? `${plan.maximumCoveragePercentage}%` : 'Not applicable'} /></Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}><FinancialSupportReadOnlyDetail label="Disbursement Schedule" value={plan.disbursementScheduleApplicable ? plan.disbursementSchedule : 'Not applicable'} /></Grid>
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }}><FinancialSupportReadOnlyDetail label="Equity / Revenue Share" value={plan.equityRevenueShareApplicable ? `${plan.equityRevenueShare}%` : 'Not applicable'} /></Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Stack>
                </Box>
              )}
              <Box sx={wizardSectionCardSx}>
                <FinancialSupportSectionHeader
                  title={savedPlans.length ? 'Add Another Plan' : 'Plan Details'}
                  helperText="Create one plan at a time. When you add another plan, the configured plans above stay collapsed so the editor remains easy to scan."
                />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FieldLabel label="Plan Name" />
                    {renderTextField('planName', 'Enter plan name')}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FieldLabel label="Support Type" required />
                    {renderSelectField('supportType', supportTypeOptions, 'Select support type', false, applySupportTypePreset)}
                    <Typography sx={{ mt: 1, fontSize: 12, lineHeight: '16px', letterSpacing: '0.4px', color: '#6B6880' }}>
                      Prepopulated from Basic Info. You can still adjust it for this specific plan if needed.
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FieldLabel label="Plan Amount" />
                    {renderTextField('planAmount', '0', watchedCurrency || 'Amount')}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FieldLabel label="Currency" required />
                    {renderSelectField('currency', currencyOptions, 'Select currency')}
                    <Typography sx={{ mt: 1, fontSize: 12, lineHeight: '16px', letterSpacing: '0.4px', color: '#6B6880' }}>
                      Auto-filled from step 1 as a reminder so every plan stays aligned with the program setup.
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FieldLabel label="Plan Status" />
                    {renderSelectField('planStatus', planStatusOptions, 'Select plan status')}
                  </Grid>
                  <Grid size={12}>
                    <FieldLabel label="Description / Notes" />
                    {renderTextField('description', 'Add internal notes or short description', undefined, false, true, 4)}
                  </Grid>
                </Grid>
              </Box>
              <Box sx={wizardSectionCardSx}>
                <FinancialSupportSectionHeader title="Repayment And Fees" helperText="Use these fields for repayment-based financial support such as credit plans. Mark fields as Not Applicable for non-repayment plans." emphasized={emphasizedSections.repayment} />
                <ApplicabilityFieldRow control={control} applicableName="interestRateApplicable" label="Interest Rate" helperText="Use this for percentage-based interest charged on the plan amount.">{renderTextField('interestRate', '0', '%', !interestRateApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="repaymentDurationApplicable" label="Repayment Duration" helperText="Total repayment period in months.">{renderTextField('repaymentDuration', '0', 'months', !repaymentDurationApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="numberOfInstallmentsApplicable" label="Number of Installments" helperText="Total number of payments the beneficiary must make.">{renderTextField('numberOfInstallments', '0', undefined, !numberOfInstallmentsApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="paymentFrequencyApplicable" label="Payment Frequency" helperText="How often repayments are expected.">{renderSelectField('paymentFrequency', paymentFrequencyOptions, 'Select payment frequency', !paymentFrequencyApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="gracePeriodApplicable" label="Grace Period" helperText="Optional delay before repayment begins.">{renderTextField('gracePeriod', '0', 'days', !gracePeriodApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="processingFeeAmountApplicable" label="Processing Fee Amount" helperText="Administrative fee charged during plan activation or approval.">{renderTextField('processingFeeAmount', '0', watchedCurrency || 'Currency', !processingFeeAmountApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="latePaymentFeeAmountApplicable" label="Late Payment Fee Amount" helperText="Penalty fee charged for overdue installments.">{renderTextField('latePaymentFeeAmount', '0', watchedCurrency || 'Currency', !latePaymentFeeAmountApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="collateralRequiredApplicable" label="Collateral Required" helperText="Specify whether collateral selection is relevant for this plan.">{renderSelectField('collateralRequired', yesNoOptions, 'Select yes or no', !collateralRequiredApplicable)}</ApplicabilityFieldRow>
                {collateralRequiredApplicable && collateralRequired === 'Yes' && (
                  <>
                    <Divider />
                    <Grid container spacing={2.5} alignItems="start" sx={{ py: 2.5 }}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography sx={{ fontSize: 15, lineHeight: '22px', letterSpacing: '0.1px', fontWeight: 600, color: '#1C1B1F', mb: 0.75 }}>Collateral Details</Typography>
                        <Typography sx={{ fontSize: 13, lineHeight: '18px', letterSpacing: '0.1px', color: '#6B6880' }}>Required only when collateral is relevant and set to Yes.</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={wizardDependentFieldWrapSx}>
                          <Typography sx={wizardDependentFieldTextSx}>Dependent field</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 5 }}>
                        {renderTextField('collateralDetails', 'Describe required collateral')}
                      </Grid>
                    </Grid>
                  </>
                )}
              </Box>
              <Box sx={wizardSectionCardSx}>
                <FinancialSupportSectionHeader title="Grant And Subsidy Terms" helperText="Use these fields for grant, subsidy, reimbursement, or partial support programs." emphasized={emphasizedSections.grant} />
                <ApplicabilityFieldRow control={control} applicableName="maximumCoveragePercentageApplicable" label="Maximum Coverage Percentage" helperText="Defines what share of the eligible cost the program can cover.">{renderTextField('maximumCoveragePercentage', '0', '%', !maximumCoveragePercentageApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="matchingContributionRequiredApplicable" label="Matching Contribution Required" helperText="Specify whether the applicant must contribute part of the cost.">{renderSelectField('matchingContributionRequired', yesNoOptions, 'Select yes or no', !matchingContributionRequiredApplicable)}</ApplicabilityFieldRow>
                {matchingContributionRequiredApplicable && matchingContributionRequired === 'Yes' && (
                  <>
                    <Divider />
                    <Grid container spacing={2.5} alignItems="start" sx={{ py: 2.5 }}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography sx={{ fontSize: 15, lineHeight: '22px', letterSpacing: '0.1px', fontWeight: 600, color: '#1C1B1F', mb: 0.75 }}>Matching Contribution Percentage</Typography>
                        <Typography sx={{ fontSize: 13, lineHeight: '18px', letterSpacing: '0.1px', color: '#6B6880' }}>Required only when matching contribution is both applicable and set to Yes.</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={wizardDependentFieldWrapSx}>
                          <Typography sx={wizardDependentFieldTextSx}>Dependent field</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 5 }}>
                        {renderTextField('matchingContributionPercentage', '0', '%')}
                      </Grid>
                    </Grid>
                  </>
                )}
                <Divider />
                <ApplicabilityFieldRow control={control} applicableName="disbursementScheduleApplicable" label="Disbursement Schedule" helperText="Choose how the funds are released for this plan.">{renderSelectField('disbursementSchedule', disbursementScheduleOptions, 'Select disbursement schedule', !disbursementScheduleApplicable)}</ApplicabilityFieldRow>
                {disbursementScheduleApplicable && disbursementSchedule === 'Custom' && (
                  <>
                    <Divider />
                    <Grid container spacing={2.5} alignItems="start" sx={{ py: 2.5 }}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Typography sx={{ fontSize: 15, lineHeight: '22px', letterSpacing: '0.1px', fontWeight: 600, color: '#1C1B1F', mb: 0.75 }}>Custom Schedule Explanation</Typography>
                        <Typography sx={{ fontSize: 13, lineHeight: '18px', letterSpacing: '0.1px', color: '#6B6880' }}>Provide a short operational explanation for the custom disbursement logic.</Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={wizardDependentFieldWrapSx}>
                          <Typography sx={wizardDependentFieldTextSx}>Dependent field</Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 5 }}>
                        {renderTextField('customDisbursementSchedule', 'Describe the custom schedule')}
                      </Grid>
                    </Grid>
                  </>
                )}
              </Box>
              <Box sx={wizardSectionCardSx}>
                <FinancialSupportSectionHeader title="Investment Terms" helperText="Use these fields for investment-based support models such as equity or revenue share." emphasized={emphasizedSections.investment} />
                <ApplicabilityFieldRow control={control} applicableName="equityRevenueShareApplicable" label="Equity / Revenue Share" helperText="Applicable for equity participation or revenue-share support structures.">{renderTextField('equityRevenueShare', '0', '%', !equityRevenueShareApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="expectedReturnRateApplicable" label="Expected Return Rate" helperText="Projected return expected from the investment arrangement.">{renderTextField('expectedReturnRate', '0', '%', !expectedReturnRateApplicable)}</ApplicabilityFieldRow><Divider />
                <ApplicabilityFieldRow control={control} applicableName="exitVestingPeriodApplicable" label="Exit / Vesting Period" helperText="Months until the exit or vesting milestone is reached.">{renderTextField('exitVestingPeriod', '0', 'months', !exitVestingPeriodApplicable)}</ApplicabilityFieldRow>
              </Box>
              <Box sx={wizardSectionCardSx}>
                <FinancialSupportSectionHeader title="Additional Conditions" helperText="Use these qualitative fields to capture business rules and operational notes." />
                <Grid container spacing={3}>
                  <Grid size={12}><FieldLabel label="Eligibility Criteria" />{renderTextField('eligibilityCriteria', 'Describe who is eligible for this plan', undefined, false, true, 4)}</Grid>
                  <Grid size={12}><FieldLabel label="Disbursement Conditions" />{renderTextField('disbursementConditions', 'Describe conditions required before funds are disbursed', undefined, false, true, 4)}</Grid>
                  <Grid size={12}><FieldLabel label="Repayment Notes / Terms" />{renderTextField('repaymentNotes', 'Add repayment-related notes, special handling, or policy details', undefined, false, true, 4)}</Grid>
                  <Grid size={12}><FieldLabel label="Other Conditions" />{renderTextField('otherConditions', 'Add any additional plan conditions', undefined, false, true, 4)}</Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  onClick={addAnotherPlan}
                  sx={wizardPositiveActionButtonSx}
                  startIcon={<Box component="span" className="material-symbols-rounded" sx={mediumMaterialIconSx}>add</Box>}
                >
                  Add Another Plan
                </Button>
              </Box>
              <Box sx={wizardStickyFooterSx}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button onClick={() => navigate('/financialSupport')} sx={wizardTextActionButtonSx}>
                    Cancel
                  </Button>
                  <Button onClick={() => navigate('/financialSupport/create-program/contribution-rules')} sx={wizardSecondaryButtonSx}>
                    Previous
                  </Button>
                </Box>
                <Button
                  variant="contained"
                  onClick={goToReview}
                  sx={wizardPrimaryButtonSx}
                >
                  Next
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <AlertToast open={!!snackbarMessage} onClose={() => setSnackbarMessage('')} message={snackbarMessage} variant="success" />
    </Box>
  );
}
